import { FormEvent, useEffect, useState } from "react";
import type { ReceiptSummary } from "@accountsreceiptable/shared";

type Session = {
  user: { id: string; email: string; displayName: string };
  workspace: { id: string; name: string };
  workspaceId: string;
  role: string;
};

const reviewReceipts: ReceiptSummary[] = [
  { id: "demo-corner", merchantName: "Corner Market", transactionDate: "Today", total: 14.75, state: "needs_review" },
  { id: "demo-harbor", merchantName: "Harbor Hardware", transactionDate: "Yesterday", total: 82.4, state: "needs_review" },
];

const authMessages: Record<string, string> = {
  credentials_invalid: "That email and password do not match an account.",
  email_already_registered: "An account already exists for that email address.",
  email_invalid: "Enter a valid email address.",
  password_policy: "Use a password with at least 12 characters.",
  profile_required: "Your name and workspace name are required.",
  csrf_rejected: "Your session could not be verified. Refresh the page and try again.",
};

async function currentSession(): Promise<Session | null> {
  const response = await fetch("/api/auth/me", { headers: { accept: "application/json" } });
  if (response.status === 401) return null;
  if (!response.ok) throw new Error("session_unavailable");
  return response.json();
}

function routeTo(path: string) {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function Brand() {
  return <a className="brand" href="/" onClick={(event) => { event.preventDefault(); routeTo("/"); }} aria-label="Accounts Receiptable home"><span className="brand-mark">AR</span><span>Accounts Receiptable</span></a>;
}

function PublicHome() {
  return (
    <main className="public-shell">
      <header className="public-header"><Brand /><nav aria-label="Account navigation"><a href="/login">Sign in</a><a className="button-link" href="/register">Create account</a></nav></header>
      <section className="public-hero">
        <div>
          <p className="eyebrow">Receipts in. Tax-ready records out.</p>
          <h1>Keep the paperwork moving without giving up your privacy.</h1>
          <p className="lede">Create an account with your email and password, then capture, review, and organize business receipts in one workspace.</p>
          <div className="hero-actions"><a className="button-link" href="/register">Create your workspace</a><a className="text-link" href="/login">Sign in to your account</a></div>
        </div>
        <aside className="trust-card" aria-label="Account security summary">
          <p className="eyebrow">Your account, directly with us</p>
          <h2>No social login required.</h2>
          <ul><li>Passwords are securely hashed.</li><li>Sessions use protected, HTTP-only cookies.</li><li>Workspace access is isolated and audited.</li></ul>
        </aside>
      </section>
    </main>
  );
}

function AuthPage({ mode, onAuthenticated }: { mode: "login" | "register"; onAuthenticated: (session: Session) => void }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const registering = mode === "register";

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    const fields = new FormData(event.currentTarget);
    const input = Object.fromEntries(fields.entries());
    try {
      const response = await fetch(`/api/auth/${mode}`, {
        method: "POST",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify(input),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || "auth_failed");
      const session = await currentSession();
      if (!session) throw new Error("session_unavailable");
      onAuthenticated(session);
      routeTo("/app");
    } catch (error) {
      const code = error instanceof Error ? error.message : "auth_failed";
      setMessage(authMessages[code] || "We could not complete that request. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-shell">
      <header className="auth-header"><Brand /><a href={registering ? "/login" : "/register"}>{registering ? "Already have an account? Sign in" : "New here? Create an account"}</a></header>
      <section className="auth-layout">
        <div className="auth-intro"><p className="eyebrow">{registering ? "Start your workspace" : "Welcome back"}</p><h1>{registering ? "Create your account" : "Sign in"}</h1><p className="lede">{registering ? "Use an email and password—no social account or OAuth connection required." : "Continue to your receipt workspace and review queue."}</p></div>
        <form className="auth-card" onSubmit={submit} aria-busy={busy}>
          {registering && <label>Full name<input name="displayName" autoComplete="name" required maxLength={120} /></label>}
          {registering && <label>Workspace name<input name="workspaceName" autoComplete="organization" required maxLength={160} /></label>}
          <label>Email address<input name="email" type="email" autoComplete="email" required /></label>
          <label>Password<input name="password" type="password" autoComplete={registering ? "new-password" : "current-password"} required minLength={12} maxLength={256} /><small>{registering ? "At least 12 characters." : "Use the password for your Accounts Receiptable account."}</small></label>
          {message && <p className="form-error" role="alert">{message}</p>}
          <button className="primary auth-submit" type="submit" disabled={busy}>{busy ? "Working…" : registering ? "Create account" : "Sign in"}</button>
          <p className="form-switch">{registering ? <>Already registered? <a href="/login">Sign in</a>.</> : <>Need an account? <a href="/register">Create one</a>.</>}</p>
        </form>
      </section>
    </main>
  );
}

function ReviewQueue() {
  return (
    <section className="panel" id="review">
      <div className="panel-heading"><div><p className="eyebrow">Action center</p><h2>Review queue</h2></div><a href="#review">View all</a></div>
      {reviewReceipts.map((receipt) => <div className="queue-item" key={receipt.id}><span className="receipt-icon" aria-hidden="true">↗</span><div><strong>{receipt.merchantName}</strong><small>{receipt.transactionDate} · ${(receipt.total ?? 0).toFixed(2)} · {receipt.id === "demo-corner" ? "Office supplies" : "Unmapped"}</small></div><span className="pill warning">Needs review</span></div>)}
    </section>
  );
}

function Dashboard({ session, onLogout }: { session: Session; onLogout: () => Promise<void> }) {
  const initials = session.user.displayName.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase();
  return (
    <main className="app-shell">
      <header className="topbar"><Brand /><div className="account-actions"><span className="account-context"><strong>{session.workspace.name}</strong><small>{session.user.email}</small></span><button className="avatar" type="button" aria-label="Sign out" onClick={() => void onLogout()}>{initials}</button></div></header>
      <div className="layout">
        <nav className="sidebar" aria-label="Primary navigation"><a className="active" href="#dashboard">Dashboard</a><a href="#capture">Capture</a><a href="#review">Review</a><a href="#exports">Exports</a><a href="#inventory">Inventory</a><a href="#settings">Preferences</a></nav>
        <section className="content" id="dashboard" aria-labelledby="site-title">
          <div className="welcome"><div><p className="eyebrow">{session.workspace.name} · {session.role.replaceAll("_", " ")}</p><h1 id="site-title">Welcome, {session.user.displayName}.</h1><p className="lede">Keep receipts moving from inbox to tax-ready records.</p></div><button className="primary" type="button" aria-label="Add receipt">＋ Add receipt</button></div>
          <section className="metrics" aria-label="Receipt summary"><article><span className="label">Needs review</span><strong>12</strong><small>3 high confidence</small></article><article><span className="label">This month</span><strong>$4,820</strong><small>37 confirmed receipts</small></article><article><span className="label">Inbox</span><strong>4</strong><small>Awaiting processing</small></article></section>
          <div className="panels"><ReviewQueue /><section className="panel" id="capture"><div className="panel-heading"><div><p className="eyebrow">Fast capture</p><h2>Send a receipt</h2></div></div><p className="muted">Use the option that fits the moment. We’ll save it and keep processing in the background.</p><div className="capture-actions"><button type="button">Take photo</button><button type="button">Upload file</button><button type="button">Forward by email</button></div></section></div>
          <div className="panels"><section className="panel" id="exports"><div className="panel-heading"><div><p className="eyebrow">Tax-ready output</p><h2>Export center</h2></div><a href="#exports">Open</a></div><p className="muted">37 confirmed receipts · 2 exceptions need attention before export.</p><div className="export-row"><span>Jan 1 – Sep 18, 2026</span><button type="button" className="secondary">Preview CSV</button></div></section><section className="panel" id="inventory"><div className="panel-heading"><div><p className="eyebrow">Stock snapshot</p><h2>Inventory</h2></div><a href="#inventory">Open</a></div><div className="inventory-stat"><strong>18 items</strong><span>3 locations · 2 low-stock alerts</span></div><button type="button" className="secondary">Record movement</button></section></div>
          <section className="panel preferences-panel" id="settings"><div className="panel-heading"><div><p className="eyebrow">Workspace controls</p><h2>Preferences</h2></div><a href="#settings">Manage</a></div><div className="preference-list"><span><strong>Receipt inbox</strong><small>Connected and accepting email</small></span><span><strong>OCR processing</strong><small>Enabled · review low-confidence fields</small></span><span><strong>Notifications</strong><small>Email on · SMS off</small></span></div></section>
          <section className="status" aria-label="Service status"><div><span className="label">Web</span><strong>Ready</strong></div><div><span className="label">Receipt inbox</span><strong>Connected</strong></div><div><span className="label">Last export</span><strong>Not yet exported</strong></div></section>
        </section>
      </div>
    </main>
  );
}

export function App() {
  const [path, setPath] = useState(window.location.pathname);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const updatePath = () => setPath(window.location.pathname);
    window.addEventListener("popstate", updatePath);
    currentSession().then(setSession).catch(() => setSession(null)).finally(() => setLoading(false));
    return () => window.removeEventListener("popstate", updatePath);
  }, []);

  useEffect(() => {
    if (!loading && !session && path.startsWith("/app")) routeTo("/login");
  }, [loading, path, session]);

  async function logout() {
    await fetch("/api/auth/logout", { method: "POST", headers: { accept: "application/json" } });
    setSession(null);
    routeTo("/login");
  }

  if (loading) return <main className="loading-shell" aria-live="polite"><Brand /><p>Loading your workspace…</p></main>;
  if (session && (path === "/" || path.startsWith("/app") || path === "/login" || path === "/register")) return <Dashboard session={session} onLogout={logout} />;
  if (path === "/register") return <AuthPage mode="register" onAuthenticated={setSession} />;
  if (path === "/login" || path.startsWith("/app")) return <AuthPage mode="login" onAuthenticated={setSession} />;
  return <PublicHome />;
}
